/**
 * SuperAdmin Centre Insights - Healers and Patients cards that open the People list pre-filtered, and the "Showing" pill.
 * READ-ONLY, no API calls here: CentreInsights.js owns the People request and passes the chosen role and status to it
 * (GET /people?status=). The status parameter is only valid for healer roles (active|pending|inactive) and patients (active),
 * so statusFits() is also the guard CentreInsights.js uses before it adds status to a request.
 * Shared styles and helpers (S, Line, LinkLine, Card) come from CentreInsights.js via the ui prop.
 */

import React from 'react';
import { FiX } from 'react-icons/fi';

// Tenant kind (from the summary row) -> People role chip. Same roles as the chips in CentreInsights.js. A Map, so a kind
// such as "constructor" never matches.
const HEALER_ROLE = new Map([['centre', 'healer'], ['solo', 'solo_healer'], ['pool', 'pool_healer']]);
const HEALER_STATUSES = new Set(['active', 'pending', 'inactive']);
const PATIENT_STATUSES = new Set(['active']);
const ROLE_NOUN = new Map([['healer', 'healers'], ['solo_healer', 'solo healers'], ['pool_healer', 'pool healers'], ['patient', 'patients']]);
const STATUS_WORD = new Map([['active', 'Active'], ['pending', 'Pending'], ['inactive', 'Inactive']]);

const APPLICANT_NOTE = 'Applicants awaiting verification can show Pending in the list while counted as Inactive on the card.';
const PATIENT_NOTE = 'Matches the card. A patient registered under another centre can make the card slightly higher.';

/** True when this status may be sent with this role (matches the backend rule). Empty or unknown means no filter. */
export const statusFits = (role, status) => {
  if (role === 'patient') return PATIENT_STATUSES.has(status);
  return ROLE_NOUN.has(role) && HEALER_STATUSES.has(status);
};

const count = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Healers card. Plain lines unless onPeople is given; with it, every line opens the People list (role from the tenant kind). */
export function HealersCard({ ui, healers, kind, onPeople }) {
  const { Card, Line, LinkLine } = ui;
  if (!healers) return <Card title="Healers"><div style={ui.S.note}>Unavailable right now</div></Card>;
  const role = HEALER_ROLE.get(kind);
  if (!onPeople || !role) {
    return <Card title="Healers"><Line label="Total" value={healers.total} /><Line label="Active" value={healers.active} />
      <Line label="Pending" value={healers.pending} /></Card>;
  }
  // The card does not return Inactive; it is everything counted that is not active and not pending, and never negative.
  const inactive = Math.max(0, count(healers.total) - count(healers.active) - count(healers.pending));
  const open = status => () => onPeople(role, status);
  return (
    <Card title="Healers">
      <LinkLine label="Total" what="people" value={healers.total} onClick={open('')} />
      <LinkLine label="Active" what="people" value={healers.active} onClick={open('active')} />
      <LinkLine label="Pending" what="people" value={healers.pending} onClick={open('pending')} />
      {inactive > 0 && <LinkLine label="Inactive" what="people" value={inactive} onClick={open('inactive')} />}
    </Card>
  );
}

/** Patients card. Registered opens the unfiltered patient list; Active opens the Active filter. */
export function PatientsCard({ ui, patients, note, onPeople }) {
  const { Card, Line, LinkLine } = ui;
  if (!patients) return <Card title="Patients" note={note}><div style={ui.S.note}>Unavailable right now</div></Card>;
  if (!onPeople) {
    return <Card title="Patients" note={note}><Line label="Registered" value={patients.registered} />
      <Line label="Active" value={patients.active ?? '-'} /></Card>;
  }
  return (
    <Card title="Patients" note={note}>
      <LinkLine label="Registered" what="people" value={patients.registered} onClick={() => onPeople('patient', '')} />
      <LinkLine label="Active" what="people" value={patients.active ?? '-'} onClick={() => onPeople('patient', 'active')} />
    </Card>
  );
}

/** "Showing: Active healers" pill with a clear button, plus the explanatory note. Nothing is drawn without a valid filter. */
export function PeopleFilterPill({ ui, role, status, onClear }) {
  if (!statusFits(role, status)) return null;
  const { S } = ui;
  const note = role === 'patient' ? PATIENT_NOTE : status === 'active' ? null : APPLICANT_NOTE;
  return (
    <div>
      <div style={S.row}>
        <span style={{ ...S.chip(true), display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'default' }}>
          Showing: {STATUS_WORD.get(status)} {ROLE_NOUN.get(role)}
          <button type="button" onClick={onClear} aria-label="Clear status filter"
            style={{ minHeight: '44px', minWidth: '44px', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', color: 'inherit', padding: 0 }}>
            <FiX />
          </button>
        </span>
      </div>
      {note && <div style={{ ...S.note, marginTop: 0, marginBottom: '12px' }}>{note}</div>}
    </div>
  );
}
